import {useContext, useState, MutableRefObject, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import i18n from 'i18next';
import {Box} from '@mui/material';
import {useParams, useRouteMatch} from 'react-router-dom';
import {UserType} from '../../../../../../../backend/enums/user-type';
import {PlaylistItemUser} from '../../../../../../../backend/responses/playlist-item-user';
import {AlertModal} from '../../../../../../../components/alert-modal/alert-modal';
import {PaginatedTable} from '../../../../../../../components/paginated-table/paginated-table';
import {SnackBarContext} from '../../../../../../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../../../../../../contexts/snack-bar/types';
import {ScreenPaths} from '../../../../../../../enums/screen-path';
import {usePaginatedTable} from '../../../../../../../hooks/paginated-table';
import {translations} from '../../../../../../../i18n/translations';
import {AppLanguageTag} from '../../../../../../../i18n/types';
import {PaginatedTableData} from '../../../../../../../interfaces/paginated-table-data';
import {PlaylistsRepository} from '../../../../../../../repositories/playlists';
import {OfficeContext} from '../../../../../../../contexts/office/context';
import {OfficeContextType} from '../../../../../../../contexts/office/types';
import SearchInput from '../../../../../../../components/search-input/search-input';
import GenericHeader from '../../../../../../../components/generic-header/generic-header';
import {SectionTabs} from '../../../../../../../components/section-tabs/section-tabs';

// Columns IDs
enum ColumnIdentifier {
  Name = 'name',
  Type = 'type',
  State = 'state',
}

export default function PlaylistItemUsersProfessionalCaregiverOfficeScreen({
  canWrite,
}: {
  canWrite: boolean;
}) {
  // Get params
  // - id: playlist id
  // - entityId: entity id
  // - itemId: playlist item id
  let {playlistId, entityId, itemId}: {playlistId?: string; entityId?: string; itemId?: string} =
    useParams();

  // Get context
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const officeContext: OfficeContextType = useContext(OfficeContext);

  let {url} = useRouteMatch();

  // Hold state
  const [updatePlaylistItemUserDialogOpened, setUpdatePlaylistItemUserDialogOpened] = useState<{
    disableDialogOpened: boolean;
    enableDialogOpened: boolean;
  }>({disableDialogOpened: false, enableDialogOpened: false});

  // Hold ref
  const dataToUpdateRef: MutableRefObject<
    {userId: string; userName: string; playlistItemActiveFlag: boolean} | undefined
  > = useRef();

  // Translations
  const {t} = useTranslation();

  // Fetch users
  const handleGetUsers = async (limit: number, skip: number, search?: string) => {
    // Check ids
    if (!playlistId || !entityId || !itemId) {
      // Error
      throw new Error('Empty entity, playlist, or playlist item id.');
    }

    // Load users
    const result: {totalCount: number; data: Array<PlaylistItemUser>} =
      await PlaylistsRepository.instance.getPlaylistItemUsersInEntityAsAffiliatedForEntity(
        entityId,
        playlistId,
        itemId,
        skip,
        limit,
        search
      );

    // Process data
    const processedData: PaginatedTableData<PlaylistItemUser> = result.data.map((obj) => {
      // Link
      let toLink: string = '';
      if (obj.user.type === UserType.Senior) {
        toLink = `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}/${obj.user.id}`;
      } else if (obj.user.type === UserType.Caregiver) {
        toLink = `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}/${obj.user.id}${ScreenPaths.Senior_Profile}`;
      }

      // Return row values
      return {
        id: obj.user.id || '',
        item: obj,
        avatarUrl: obj.user.picture,
        enabled: obj.isActive === true ? true : false,
        toLink: toLink,
        columns: [
          {
            columnId: ColumnIdentifier.Name,
            value: obj.user.name || '',
          },
          {
            columnId: ColumnIdentifier.Type,
            value: obj.user.type ? t(translations.user_types[obj.user.type]) : '',
          },
          {
            columnId: ColumnIdentifier.State,
            value:
              obj.isActive === true
                ? t(
                    translations.playlists.professional_caregiver.playlist_item.users
                      .state_value_true
                  )
                : t(
                    translations.playlists.professional_caregiver.playlist_item.users
                      .state_value_false
                  ),
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Use the paginated table hook
  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handlePageChange,
    handleApplyFilters,
    handleUpdateCurrentSearch,
    handleUpdateItem,
  } = usePaginatedTable<PlaylistItemUser>({
    fetchData: handleGetUsers,
  });

  const handleAskUpdatePlaylistItemUserState = (userId: string, value: boolean) => {
    // Get user name
    const userName: string | undefined = data
      .find((obj) => obj.id === userId)
      ?.columns.find((obj) => obj.columnId === ColumnIdentifier.Name)?.value;

    // Set user info for later
    dataToUpdateRef.current = {
      userId: userId,
      userName: userName || '',
      playlistItemActiveFlag: value,
    };

    // Open modal
    setUpdatePlaylistItemUserDialogOpened({
      disableDialogOpened: !value,
      enableDialogOpened: value,
    });
  };

  const handleDeclineUpdatePlaylistItemUserState = () => {
    // Close modal
    setUpdatePlaylistItemUserDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Clear
    dataToUpdateRef.current = undefined;
  };

  const handleConfirmUpdatePlaylistItemUserState = () => {
    // Close modal
    setUpdatePlaylistItemUserDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Update
    handleUpdatePlaylistItemUserState();
  };

  const handleUpdatePlaylistItemUserState = () => {
    if (dataToUpdateRef.current) {
      // Keep values
      const userId = dataToUpdateRef.current.userId;
      const value = dataToUpdateRef.current.playlistItemActiveFlag;

      // Clear
      dataToUpdateRef.current = undefined;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === userId)?.item;
      if (updatedItem) {
        updatedItem.isActive = value;
      }

      // Update item in UI
      handleUpdateItem(
        userId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: value
              ? t(
                  translations.playlists.professional_caregiver.playlist_item.users.state_value_true
                )
              : t(
                  translations.playlists.professional_caregiver.playlist_item.users
                    .state_value_false
                ),
          },
        ],
        updatedItem,
        value
      );

      // Make request to the backend
      // Note: Don't await because it will take too much time to update
      continueUpdatePlaylistItemUserState(userId, playlistId || '', itemId || '', value);
    } else {
      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(
          translations.playlists.professional_caregiver.playlist_item.users.errors
            .update_state_item_error_message
        ),
      });

      // Not expected. Log.
      console.debug(
        '🔴 Could not update the state of the playlist item. Information was missing to perform the request.'
      );
    }
  };

  const continueUpdatePlaylistItemUserState = async (
    userId: string,
    playlistId: string,
    playlistItemId: string,
    value: boolean
  ) => {
    try {
      // Make request to the backend
      await PlaylistsRepository.instance.updatePlaylistItemActiveStateForUserAsAffiliatedForEntity(
        userId,
        playlistId,
        playlistItemId,
        value
      );
    } catch (error) {
      // Old flag
      const oldFlag: boolean = !value;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === userId)?.item;
      if (updatedItem) {
        updatedItem.isActive = oldFlag;
      }

      // Change item back
      handleUpdateItem(
        userId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: oldFlag
              ? t(
                  translations.playlists.professional_caregiver.playlist_item.users.state_value_true
                )
              : t(
                  translations.playlists.professional_caregiver.playlist_item.users
                    .state_value_false
                ),
          },
        ],
        updatedItem,
        oldFlag
      );

      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: value
          ? t(
              translations.playlists.professional_caregiver.playlist_item.users.errors
                .enable_item_error_message
            )
          : t(
              translations.playlists.professional_caregiver.playlist_item.users.errors
                .disable_item_error_message
            ),
      });

      // Log
      console.debug(
        `🔴 Could not update the state of the playlist item with id ${playlistItemId} to ${value} for user with id ${userId}.`,
        error
      );
    }
  };

  // Get playlist item name
  let playlistItemNameLocalized: string = '';
  switch (i18n.language) {
    case AppLanguageTag.ptPT:
      playlistItemNameLocalized =
        officeContext.state.playlistItemForm?.titleLocalized?.ptPT ||
        officeContext.state.playlistItemForm?.title ||
        '';
      break;
    case AppLanguageTag.esES:
      playlistItemNameLocalized =
        officeContext.state.playlistItemForm?.titleLocalized?.esES ||
        officeContext.state.playlistItemForm?.title ||
        '';
      break;
    case AppLanguageTag.glES:
      playlistItemNameLocalized =
        officeContext.state.playlistItemForm?.titleLocalized?.glES ||
        officeContext.state.playlistItemForm?.title ||
        '';
      break;
    case AppLanguageTag.enUS:
      playlistItemNameLocalized =
        officeContext.state.playlistItemForm?.titleLocalized?.enUS ||
        officeContext.state.playlistItemForm?.title ||
        '';
      break;
    case AppLanguageTag.ptBR:
      playlistItemNameLocalized =
        officeContext.state.playlistItemForm?.titleLocalized?.ptBR ||
        officeContext.state.playlistItemForm?.title ||
        '';
      break;
    default:
      playlistItemNameLocalized = officeContext.state.playlistItemForm?.title || '';
      break;
  }

  const baseUrl = `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion}`;
  const playlistUrl = `${baseUrl}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Playlists}/${playlistId}`;
  const playlistItemUrl = `${playlistUrl}/items/${itemId}`;

  return (
    <>
      <GenericHeader
        isLoading={!officeContext.state.playlistForm || !officeContext.state.playlistItemForm}
        titleCrumbs={[
          {
            label: t(translations.office.professional_caregiver.tabs.digital_inclusion),
            link: baseUrl,
          },
          {
            label: t(translations.office.professional_caregiver.tabs.playlists),
            link: `${baseUrl}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Playlists}`,
          },
          {
            label: officeContext.state.playlistForm?.name || '',
            link: playlistUrl,
          },
          {
            label: officeContext.state.playlistItemForm?.title || '',
            link: playlistItemUrl,
          },
          {
            label: t(translations.playlists.professional_caregiver.edit.tabs.users),
            link: url,
          },
        ]}
        leftColumn={
          <>
            <SearchInput
              placeholder={t(
                translations.playlists.professional_caregiver.root.search_input_placeholder
              )}
              value={currentSearch}
              onChange={(event) => handleUpdateCurrentSearch(event.target.value)}
              onSearchAction={(value: string) => {
                handleApplyFilters(1, value);
              }}
            />
          </>
        }
        rightColumn={null}
      />

      <Box className="listing-container">
        <Box className="listing-container-centered">
          <Box sx={{backgroundColor: 'white', paddingBottom: '16px'}}>
            <SectionTabs
              currentTab="users"
              tabs={[
                {
                  value: 'details',
                  label: t(
                    translations.playlists.professional_caregiver.playlist_item.tabs.details
                  ),
                  href: `${playlistItemUrl}/details`,
                },
                {
                  value: 'users',
                  label: t(translations.playlists.professional_caregiver.playlist_item.tabs.users),
                  href: `${playlistItemUrl}/users`,
                },
              ]}
              exitUrl={`${url.replace(`/items/${itemId}`, '/items')}`}
            />
          </Box>

          <PaginatedTable
            avatar={{
              type: 'user',
            }}
            emptyTableMessage={t(
              translations.playlists.professional_caregiver.playlist_item.users.no_users
            )}
            columns={[
              {
                columnId: ColumnIdentifier.Name,
                title: t(
                  translations.playlists.professional_caregiver.playlist_item.users.name_header
                ),
                flexGrow: 2,
              },
              {
                columnId: ColumnIdentifier.Type,
                title: t(
                  translations.playlists.professional_caregiver.playlist_item.users.type_header
                ),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.State,
                title: t(
                  translations.playlists.professional_caregiver.playlist_item.users.state_header
                ),
                flexGrow: 1,
                button: {
                  type: 'toggle',
                  showColumnValue: true,
                  toggle: {
                    enabledValue: t(
                      translations.playlists.professional_caregiver.playlist_item.users
                        .state_value_true
                    ),
                    handleClick: handleAskUpdatePlaylistItemUserState,
                  },
                  disabled: !canWrite,
                  disabledTooltip: t(translations.user_playlist_items.state_disabled_tooltip),
                },
              },
            ]}
            rows={data}
            isLoading={isLoading}
            error={error}
            pagesCount={pagesCount}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            linkableRows={false}
          />
        </Box>
      </Box>
      <AlertModal
        id="confirm-disable-playlist-item-user-state"
        opened={updatePlaylistItemUserDialogOpened.disableDialogOpened}
        title={t(
          translations.playlists.professional_caregiver.playlist_item.users
            .disable_item_dialog_title,
          {
            name: playlistItemNameLocalized,
          }
        )}
        content={t(
          translations.playlists.professional_caregiver.playlist_item.users
            .disable_item_dialog_message,
          {
            name: dataToUpdateRef.current?.userName || '',
          }
        )}
        handleClose={handleDeclineUpdatePlaylistItemUserState}
        actions={[
          {
            label: t(
              translations.playlists.professional_caregiver.playlist_item.users
                .disable_item_dialog_action_yes_button
            ),
            type: 'destructive',
            handleClick: handleConfirmUpdatePlaylistItemUserState,
          },
          {
            label: t(
              translations.playlists.professional_caregiver.playlist_item.users
                .disable_item_dialog_action_no_button
            ),
            type: 'neutral',
            handleClick: handleDeclineUpdatePlaylistItemUserState,
          },
        ]}
      />
      <AlertModal
        id="confirm-enable-playlist-item-user-state"
        opened={updatePlaylistItemUserDialogOpened.enableDialogOpened}
        title={t(
          translations.playlists.professional_caregiver.playlist_item.users
            .enable_item_dialog_title,
          {
            name: playlistItemNameLocalized,
          }
        )}
        content={t(
          translations.playlists.professional_caregiver.playlist_item.users
            .enable_item_dialog_message,
          {
            name: dataToUpdateRef.current?.userName || '',
          }
        )}
        handleClose={handleDeclineUpdatePlaylistItemUserState}
        actions={[
          {
            label: t(
              translations.playlists.professional_caregiver.playlist_item.users
                .enable_item_dialog_action_no_button
            ),
            type: 'neutral',
            handleClick: handleDeclineUpdatePlaylistItemUserState,
          },
          {
            label: t(
              translations.playlists.professional_caregiver.playlist_item.users
                .enable_item_dialog_action_yes_button
            ),
            type: 'nondestructive',
            handleClick: handleConfirmUpdatePlaylistItemUserState,
          },
        ]}
      />
    </>
  );
}
